import React, { useState, useEffect, useCallback } from 'react';
import { Checkbox } from '@/components/ui/Checkbox';
import { LoadingSpinner } from '@/components/ui';
import { cn } from '@/utils/cn';
import { GEO_TARGET_OPTIONS, LANGUAGE_OPTIONS } from './networkOptions';
import { type TargetingSettings } from './NetworkSettingsStep';
import {
  DemographicTargeting,
  type DemographicSettings,
} from '@/components/features/AudienceTargeting/DemographicTargeting';
import { campaignsApi } from '@/services/api/campaigns';
import type {
  ChannelStrategy,
  DemandGenChannelSettings,
  DemandGenSettings,
} from './demandGenSettings';

interface AvailableAudience {
  resource_name: string;
  audience_id: string;
  audience_name: string;
  audience_type: string;
  description?: string;
}

interface DemandGenSettingsStepProps {
  demandGenSettings: DemandGenSettings;
  targetingSettings: TargetingSettings;
  onChangeDemandGenSettings: (settings: DemandGenSettings) => void;
  onChangeTargetingSettings: (settings: TargetingSettings) => void;
  campaignId?: string; // Existing campaign ID for fetching available audiences (optional for new campaigns)
}

const CHANNEL_STRATEGY_OPTIONS: Array<{
  value: ChannelStrategy;
  label: string;
  description: string;
}> = [
  {
    value: 'ALL_CHANNELS',
    label: 'All Channels',
    description: 'Deliver on all Google-owned channels plus partner networks. Recommended for maximum reach.',
  },
  {
    value: 'ALL_OWNED_AND_OPERATED_CHANNELS',
    label: 'Google-Owned Channels Only',
    description: 'Deliver on YouTube, Discover, Gmail, and Maps. Excludes third-party Display network.',
  },
  {
    value: 'SELECTED_CHANNELS',
    label: 'Custom Selection',
    description: 'Choose specific channels where your ads will appear.',
  },
];

const CHANNEL_OPTIONS: Array<{
  key: keyof Omit<DemandGenChannelSettings, 'channelStrategy'>;
  label: string;
  description: string;
  icon: string;
}> = [
  {
    key: 'youtubeInStream',
    label: 'YouTube In-Stream',
    description: 'Skippable video ads before, during, or after YouTube videos',
    icon: 'youtube',
  },
  {
    key: 'youtubeInFeed',
    label: 'YouTube In-Feed',
    description: 'Video ads in YouTube search results and watch next',
    icon: 'youtube',
  },
  {
    key: 'youtubeShorts',
    label: 'YouTube Shorts',
    description: 'Ads between short-form vertical videos on YouTube Shorts',
    icon: 'youtube',
  },
  {
    key: 'discover',
    label: 'Discover',
    description: 'Ads in the Google Discover feed on mobile devices',
    icon: 'discover',
  },
  {
    key: 'gmail',
    label: 'Gmail',
    description: 'Expandable ads in Gmail promotions and social tabs',
    icon: 'gmail',
  },
  {
    key: 'display',
    label: 'Display Network',
    description: 'Ads on third-party websites and apps in Google Display Network',
    icon: 'display',
  },
  // Maps is not yet supported in Google Ads API v22/v23
  // {
  //   key: 'maps',
  //   label: 'Google Maps',
  //   description: 'Promoted pins and location-based ads on Google Maps',
  //   icon: 'maps',
  // },
];

export const DemandGenSettingsStep: React.FC<DemandGenSettingsStepProps> = ({
  demandGenSettings,
  targetingSettings,
  onChangeDemandGenSettings,
  onChangeTargetingSettings,
  campaignId,
}) => {
  const { channelSettings, optimizedTargeting, initialAudiences, demographicTargeting } = demandGenSettings;

  // State for audience selection
  const [showAudienceSelector, setShowAudienceSelector] = useState(false);
  const [availableAudiences, setAvailableAudiences] = useState<AvailableAudience[]>([]);
  const [audienceSearch, setAudienceSearch] = useState('');
  const [isLoadingAudiences, setIsLoadingAudiences] = useState(false);
  const [audienceError, setAudienceError] = useState<string | null>(null);

  // Fetch available audiences when selector is opened
  useEffect(() => {
    if (showAudienceSelector && campaignId) {
      const fetchAudiences = async () => {
        setIsLoadingAudiences(true);
        setAudienceError(null);
        try {
          const data = await campaignsApi.getAvailableAudiences(campaignId);
          setAvailableAudiences(data.audiences || []);
        } catch (err) {
          console.error('Failed to fetch audiences:', err);
          setAudienceError('Failed to load audiences');
        } finally {
          setIsLoadingAudiences(false);
        }
      };
      fetchAudiences();
    }
  }, [showAudienceSelector, campaignId]);

  // Filter audiences based on search
  const filteredAudiences = availableAudiences.filter(
    (a) => !audienceSearch || a.audience_name.toLowerCase().includes(audienceSearch.toLowerCase())
  );

  // Check if an audience is already selected
  const isAudienceSelected = useCallback((resourceName: string) => {
    return initialAudiences.some((a) => a.resource_name === resourceName);
  }, [initialAudiences]);

  // Toggle audience selection
  const handleToggleAudience = useCallback((audience: AvailableAudience) => {
    const isSelected = isAudienceSelected(audience.resource_name);
    const newAudiences = isSelected
      ? initialAudiences.filter((a) => a.resource_name !== audience.resource_name)
      : [...initialAudiences, {
          resource_name: audience.resource_name,
          audience_name: audience.audience_name,
          audience_type: audience.audience_type,
        }];

    onChangeDemandGenSettings({
      ...demandGenSettings,
      initialAudiences: newAudiences,
    });
  }, [demandGenSettings, initialAudiences, isAudienceSelected, onChangeDemandGenSettings]);

  // Handle demographic targeting changes
  const handleDemographicChange = useCallback((newDemographics: DemographicSettings) => {
    onChangeDemandGenSettings({
      ...demandGenSettings,
      demographicTargeting: newDemographics,
    });
  }, [demandGenSettings, onChangeDemandGenSettings]);

  // Remove a selected audience
  const handleRemoveAudience = useCallback((resourceName: string) => {
    onChangeDemandGenSettings({
      ...demandGenSettings,
      initialAudiences: initialAudiences.filter((a) => a.resource_name !== resourceName),
    });
  }, [demandGenSettings, initialAudiences, onChangeDemandGenSettings]);

  const handleChannelStrategyChange = (strategy: ChannelStrategy) => {
    // When switching to a strategy, set default channel values
    let newChannelSettings: DemandGenChannelSettings;

    if (strategy === 'ALL_CHANNELS') {
      newChannelSettings = {
        channelStrategy: strategy,
        youtubeInStream: true,
        youtubeInFeed: true,
        youtubeShorts: true,
        discover: true,
        gmail: true,
        display: true, // ALL_CHANNELS includes Display
        // maps: true,  // Not yet supported in Google Ads API v22/v23
      };
    } else if (strategy === 'ALL_OWNED_AND_OPERATED_CHANNELS') {
      newChannelSettings = {
        channelStrategy: strategy,
        youtubeInStream: true,
        youtubeInFeed: true,
        youtubeShorts: true,
        discover: true,
        gmail: true,
        display: false, // Excludes third-party Display
        // maps: true,  // Not yet supported in Google Ads API v22/v23
      };
    } else {
      // SELECTED_CHANNELS - keep current selections or default to all on
      newChannelSettings = {
        ...channelSettings,
        channelStrategy: strategy,
      };
    }

    onChangeDemandGenSettings({
      ...demandGenSettings,
      channelSettings: newChannelSettings,
    });
  };

  const handleChannelToggle = (channel: keyof Omit<DemandGenChannelSettings, 'channelStrategy'>) => {
    onChangeDemandGenSettings({
      ...demandGenSettings,
      channelSettings: {
        ...channelSettings,
        [channel]: !channelSettings[channel],
      },
    });
  };

  const handleOptimizedTargetingToggle = () => {
    onChangeDemandGenSettings({
      ...demandGenSettings,
      optimizedTargeting: !optimizedTargeting,
    });
  };

  const handleGeoToggle = (geoId: number) => {
    const newGeoIds = targetingSettings.geoTargetIds.includes(geoId)
      ? targetingSettings.geoTargetIds.filter((id) => id !== geoId)
      : [...targetingSettings.geoTargetIds, geoId];

    onChangeTargetingSettings({
      ...targetingSettings,
      geoTargetIds: newGeoIds,
    });
  };

  const handleLanguageToggle = (langId: number) => {
    const newLangIds = targetingSettings.languageIds.includes(langId)
      ? targetingSettings.languageIds.filter((id) => id !== langId)
      : [...targetingSettings.languageIds, langId];

    onChangeTargetingSettings({
      ...targetingSettings,
      languageIds: newLangIds,
    });
  };

  const handleSelectAllEnglishSpeaking = () => {
    const englishCountries = [2840, 2826, 2124, 2036, 2372, 2554];
    onChangeTargetingSettings({
      geoTargetIds: englishCountries,
      languageIds: [1000],
    });
  };

  // Count enabled channels for display
  const enabledChannelCount = CHANNEL_OPTIONS.filter(
    (opt) => channelSettings[opt.key]
  ).length;

  // Check if at least one channel is selected
  const hasAtLeastOneChannel = enabledChannelCount > 0;

  return (
    <div className="space-y-8">
      {/* Channel Strategy Section */}
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Channel Settings</h3>
          <p className="text-sm text-gray-600">
            Choose where your Demand Gen ads will appear across Google's surfaces.
          </p>
        </div>

        {/* Channel Strategy Selection */}
        <div className="space-y-3 mb-4">
          {CHANNEL_STRATEGY_OPTIONS.map((option) => (
            <div
              key={option.value}
              className={cn(
                'border-2 rounded-lg transition-all',
                channelSettings.channelStrategy === option.value
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200'
              )}
            >
              <label
                className={cn(
                  'flex items-start p-4 cursor-pointer rounded-lg',
                  'hover:bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500',
                  channelSettings.channelStrategy === option.value && 'hover:bg-blue-50'
                )}
              >
                <input
                  type="radio"
                  name="channel-strategy"
                  value={option.value}
                  checked={channelSettings.channelStrategy === option.value}
                  onChange={() => handleChannelStrategyChange(option.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 mt-0.5"
                />
                <div className="ml-3 flex-1">
                  <span className="block text-sm font-medium text-gray-900">{option.label}</span>
                  <p className="text-xs text-gray-500 mt-1">{option.description}</p>
                </div>
              </label>
            </div>
          ))}
        </div>

        {/* Individual Channel Selection (only for SELECTED_CHANNELS) */}
        {channelSettings.channelStrategy === 'SELECTED_CHANNELS' && (
          <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-medium text-gray-900">Select Channels</h4>
              <span className="text-xs text-gray-500">
                {enabledChannelCount} of {CHANNEL_OPTIONS.length} selected
              </span>
            </div>

            <div className="space-y-2">
              {CHANNEL_OPTIONS.map((channel) => (
                <label
                  key={channel.key}
                  className={cn(
                    'flex items-start p-3 rounded-lg cursor-pointer transition-colors',
                    channelSettings[channel.key]
                      ? 'bg-blue-50 border border-blue-200'
                      : 'bg-white border border-gray-200 hover:bg-gray-50'
                  )}
                >
                  <Checkbox
                    checked={channelSettings[channel.key]}
                    onChange={() => handleChannelToggle(channel.key)}
                  />
                  <div className="ml-3 flex-1">
                    <span className="block text-sm font-medium text-gray-900">{channel.label}</span>
                    <p className="text-xs text-gray-500">{channel.description}</p>
                  </div>
                </label>
              ))}
            </div>

            {!hasAtLeastOneChannel && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-xs text-red-700">
                  Please select at least one channel for your ads to appear.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Optimized Targeting Section */}
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Optimized Targeting</h3>
          <p className="text-sm text-gray-600">
            Let Google's AI find additional audiences similar to your target customers.
          </p>
        </div>

        <div className="p-4 border border-gray-200 rounded-lg">
          <label className="flex items-start cursor-pointer">
            <Checkbox
              checked={optimizedTargeting}
              onChange={handleOptimizedTargetingToggle}
            />
            <div className="ml-3 flex-1">
              <span className="block text-sm font-medium text-gray-900">
                Enable Optimized Targeting
              </span>
              <p className="text-xs text-gray-500 mt-1">
                Google will expand beyond your selected audiences to find more potential customers.
                Recommended for better campaign performance.
              </p>
            </div>
          </label>
        </div>

        {!optimizedTargeting && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <svg
                className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-xs text-yellow-700">
                Disabling optimized targeting may limit your campaign's reach and performance.
                Your audience inputs will be used as strict filters rather than signals.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Audience & Demographic Targeting Section - Only shown when editing existing campaign */}
      {campaignId ? (
        <>
          {/* Initial Audience Targeting Section */}
          <div>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Audience Targeting</h3>
              <p className="text-sm text-gray-600">
                Select audiences to target for the initial ad group. You can add more audiences later.
              </p>
            </div>

            {/* Selected Audiences */}
            {initialAudiences.length > 0 && (
              <div className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-medium text-gray-900">Selected Audiences</h4>
                  <span className="text-xs text-gray-500">{initialAudiences.length} selected</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {initialAudiences.map((audience) => (
                    <span
                      key={audience.resource_name}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-800 rounded-full text-sm"
                    >
                      {audience.audience_name}
                      <button
                        type="button"
                        onClick={() => handleRemoveAudience(audience.resource_name)}
                        className="text-blue-600 hover:text-blue-800 focus:outline-none"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Audience Selector */}
            <div>
              <button
                type="button"
                onClick={() => setShowAudienceSelector(!showAudienceSelector)}
                className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
              >
                {showAudienceSelector ? 'Hide Audience Selector' : '+ Add Audiences'}
              </button>

              {showAudienceSelector && (
                <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-white">
                  {/* Search */}
                  <input
                    type="text"
                    placeholder="Search audiences..."
                    value={audienceSearch}
                    onChange={(e) => setAudienceSearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  />

                  {/* Audience List */}
                  <div className="mt-3 max-h-60 overflow-y-auto">
                    {isLoadingAudiences ? (
                      <div className="flex justify-center py-6">
                        <LoadingSpinner size="md" />
                      </div>
                    ) : audienceError ? (
                      <div className="text-center py-4 text-red-600 text-sm">{audienceError}</div>
                    ) : filteredAudiences.length === 0 ? (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        {audienceSearch ? 'No audiences match your search' : 'No audiences available'}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {filteredAudiences.map((audience) => (
                          <label
                            key={audience.resource_name}
                            className={cn(
                              'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                              isAudienceSelected(audience.resource_name)
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            )}
                          >
                            <Checkbox
                              checked={isAudienceSelected(audience.resource_name)}
                              onChange={() => handleToggleAudience(audience)}
                            />
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-gray-900 block truncate">
                                {audience.audience_name}
                              </span>
                              <span className="text-xs text-gray-500">
                                {audience.audience_type.replace(/_/g, ' ')}
                              </span>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Demographic Targeting Section */}
          <div>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Demographic Targeting</h3>
              <p className="text-sm text-gray-600">
                Narrow your audience by age, gender, income, and parental status.
                Leave empty to target all demographics.
              </p>
            </div>

            <div className="p-4 border border-gray-200 rounded-lg bg-white">
              <DemographicTargeting
                value={demographicTargeting}
                onChange={handleDemographicChange}
                compact
              />
            </div>
          </div>
        </>
      ) : (
        /* Info message during campaign creation - audiences/demographics can only be set after campaign exists */
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Audience & Demographic Targeting</h3>
          <p className="text-sm text-gray-600">
            Audience and demographic targeting will be available after creating the campaign.
            You can configure these settings from the campaign management page once the campaign is created.
          </p>
        </div>
      )}

      {/* Location Targeting Section */}
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Location Targeting</h3>
          <p className="text-sm text-gray-600">
            Choose which countries or regions to show your ads in.
          </p>
        </div>

        {/* Quick select buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={() => onChangeTargetingSettings({ ...targetingSettings, geoTargetIds: [2840] })}
            className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
          >
            USA Only
          </button>
          <button
            type="button"
            onClick={handleSelectAllEnglishSpeaking}
            className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
          >
            English-Speaking Countries
          </button>
          <button
            type="button"
            onClick={() => onChangeTargetingSettings({ ...targetingSettings, geoTargetIds: GEO_TARGET_OPTIONS.map(g => g.id) })}
            className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
          >
            Select All
          </button>
          <button
            type="button"
            onClick={() => onChangeTargetingSettings({ ...targetingSettings, geoTargetIds: [] })}
            className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
          >
            Clear All
          </button>
        </div>

        {/* Country grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-3 border border-gray-200 rounded-lg bg-gray-50">
          {GEO_TARGET_OPTIONS.map((geo) => (
            <label
              key={geo.id}
              className={cn(
                'flex items-center p-2 rounded cursor-pointer transition-colors',
                targetingSettings.geoTargetIds.includes(geo.id)
                  ? 'bg-blue-50 border border-blue-200'
                  : 'bg-white border border-gray-200 hover:bg-gray-50'
              )}
            >
              <Checkbox
                checked={targetingSettings.geoTargetIds.includes(geo.id)}
                onChange={() => handleGeoToggle(geo.id)}
              />
              <span className="ml-2 text-sm text-gray-700">{geo.name}</span>
            </label>
          ))}
        </div>

        {targetingSettings.geoTargetIds.length === 0 && (
          <p className="mt-2 text-sm text-amber-600">
            Please select at least one location. USA will be used as default if none selected.
          </p>
        )}
      </div>

      {/* Language Targeting Section */}
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Language Targeting</h3>
          <p className="text-sm text-gray-600">
            Choose which languages your customers speak.
          </p>
        </div>

        {/* Language grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-40 overflow-y-auto p-3 border border-gray-200 rounded-lg bg-gray-50">
          {LANGUAGE_OPTIONS.map((lang) => (
            <label
              key={lang.id}
              className={cn(
                'flex items-center p-2 rounded cursor-pointer transition-colors',
                targetingSettings.languageIds.includes(lang.id)
                  ? 'bg-blue-50 border border-blue-200'
                  : 'bg-white border border-gray-200 hover:bg-gray-50'
              )}
            >
              <Checkbox
                checked={targetingSettings.languageIds.includes(lang.id)}
                onChange={() => handleLanguageToggle(lang.id)}
              />
              <span className="ml-2 text-sm text-gray-700">{lang.name}</span>
            </label>
          ))}
        </div>

        {targetingSettings.languageIds.length === 0 && (
          <p className="mt-2 text-sm text-amber-600">
            Please select at least one language. English will be used as default if none selected.
          </p>
        )}
      </div>
    </div>
  );
};
